import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import AddIcon from '@mui/icons-material/Add'

import { AddGreeting } from '@/app.interface'
import { localUserQuestionUpdate } from '@/store/questions/actions'
import { getIntent } from '@/store/questions/selector'
import { isHtmlContentEmpty } from '@/utils/markdownConverter'
import GreetingsWithGroups from '@/components/UI/GreetingsWithGroups/GreetingsWithGroups'
import PermissionWrapper from '@/components/UI/PermissionWrapper/PermissionWrapper'
import { BrainlyMathEditorSimple } from '@/components/UI/TextAreaNewVersion'

const VirtualAssistantTab = (props: any) => {
  const { t } = useTranslation('translation')
  const dispatch = useDispatch()
  const intent = useSelector(getIntent)
  const [addVirtualAssistantAnswer, setVirtualAssistantAnswer] = useState({
    text: '',
    group_ids: [],
  })
  const [virtualAssistantList, setVirtualAssistantList] = useState([])
  const [isAdding, setIsAdding] = useState(false)
  const [editorKey, setEditorKey] = useState(0) // Key to force editor remount

  // Helper function to validate content (including markdown with LaTeX math)
  const hasValidContent = (content: string): boolean => {
    // Use the imported helper that properly detects LaTeX math expressions
    return !isHtmlContentEmpty(content)
  }

  useEffect(() => {
    // Add local IDs to responses (no filtering by content)
    const responses = (intent && intent.responses) || []
    const validResponses = responses
      .filter((r: any) => r !== null && r !== undefined)
      .map((r: any, idx: number) => ({
        ...r,
        _localId: r._localId || r._id || `greeting_${idx}_${Date.now()}`,
      }))
    setVirtualAssistantList(validResponses)
  }, [intent])

  useEffect(() => {
    if (props.hasAdded) {
      setVirtualAssistantAnswer({ text: '', group_ids: [] })
    }
  }, [props.hasAdded])

  const handleVirtualAssistantAnswerChange = (htmlValue: string) => {
    const updatedAnswer = {
      ...addVirtualAssistantAnswer,
      text: htmlValue,
    }
    setVirtualAssistantAnswer(updatedAnswer)
    props.isActivityDetected && props.isActivityDetected(updatedAnswer)
  }

  ///////// Editor handlers ( LOOP )  ///////////
  const addVirtualAssistantHandler = () => {
    // Prevent double-click
    if (isAdding) {
      return
    }

    // Validate content using helper function
    const htmlContent = addVirtualAssistantAnswer?.text || ''

    if (!hasValidContent(htmlContent)) {
      return // Don't add empty variants or variants with only HTML tags
    }

    setIsAdding(true)

    const oldList = [...virtualAssistantList]
    oldList.unshift({
      text: htmlContent.trim(),
      group_ids: [],
      _localId: `greeting_${Date.now()}_${Math.random()}`,
    })
    setVirtualAssistantList([...oldList])
    dispatch(localUserQuestionUpdate('responses', oldList))

    // Clear the editor by resetting the state and incrementing key
    setVirtualAssistantAnswer({ text: '', group_ids: [] })
    setEditorKey(prev => prev + 1) // Force editor re-mount
    props.isActivityDetected && props.isActivityDetected(null)

    // Reset isAdding after a short delay
    setTimeout(() => {
      setIsAdding(false)
    }, 500)
  }

  const onUpdateGreetingsHandler = (greetings: AddGreeting[]) => {
    setVirtualAssistantList([...greetings])
    dispatch(localUserQuestionUpdate('responses', [...greetings]))
  }

  const onCopyExistingGreetingHandler = async (event: any) => {
    // Validate copied text using helper function
    const htmlContent = event?.text || ''

    if (!hasValidContent(htmlContent)) {
      return // Don't copy empty variants or variants with only HTML tags
    }

    const oldList = [...virtualAssistantList]
    oldList.unshift({
      text: htmlContent.trim(),
      group_ids: event.group_ids || [],
      _localId: `greeting_${Date.now()}_${Math.random()}`,
    })
    setVirtualAssistantList([...oldList])
    dispatch(localUserQuestionUpdate('responses', oldList))
    setVirtualAssistantAnswer({ text: '', group_ids: [] })
  }

  const renderGreetings = (
    <GreetingsWithGroups
      greetings={[...virtualAssistantList]}
      allUserGroups={[...props.allUserGroups]}
      onCopyExistingGreeting={onCopyExistingGreetingHandler}
      onUpdateGreetings={onUpdateGreetingsHandler}
    />
  )
  return (
    <div className="p-3">
      <div className="row greeting-row user-ask-ques mb-3">
        <div className="col-md-12 d-flex row-reverse align-items-start">
          <div className="ans-settings" style={{ marginLeft: '12px' }}>
            <PermissionWrapper>
              <button
                className="btn"
                onClick={addVirtualAssistantHandler}
                title={t('Add')}
              >
                <AddIcon
                  sx={{ width: 20, height: 20, color: 'primary.main' }}
                />
              </button>
            </PermissionWrapper>
          </div>
          <div className="flex-grow-1" style={{ minWidth: 0 }}>
            <BrainlyMathEditorSimple
              key={editorKey}
              value={addVirtualAssistantAnswer?.text || ''}
              onChange={handleVirtualAssistantAnswerChange}
              placeholder={t('Type answer here')}
              height="200px"
              minHeight="100px"
            />
          </div>
        </div>
      </div>
      {renderGreetings}
    </div>
  )
}

export default VirtualAssistantTab
